import type {
  HealthcareService,
  Patient,
  Practitioner,
  Reference,
} from '@medplum/fhirtypes'
import { formatTime, getNextNWorkdays } from '@seen/core/dates'
import * as appointments from '@seen/core/models/appointments/index'
import { now } from '@seen/core/utils'

import { Button, cn } from '@seen/ui'
import type { UseQueryOptions, UseQueryResult } from '@tanstack/react-query'
import { addDays, endOfDay, format, isSameMinute, startOfDay } from 'date-fns'
import { groupBy } from 'lodash-es'
import { type JSX, useEffect, useMemo, useRef } from 'react'
import { FhirQueryLoader } from '#src/components/core/FhirQueryLoader.tsx'
import { cacheKeys } from '#src/hooks/cacheKeys.ts'
import { useErrorToast } from '#src/hooks/useErrorToast.ts'
import { useMedplum } from '#src/hooks/useMedplum.ts'
import { useQuery } from '#src/hooks/useQuery.ts'

type RecommendedAppointmentSlotsProps = {
  patient: Reference<Patient> | undefined
  healthcareService: Reference<HealthcareService> | undefined
  practitioner: Reference<Practitioner> | undefined
  onAppointmentSlotSelected: (
    slot: appointments.AvailableAppointmentSlot
  ) => void
  selectedStart: string | undefined
  selectedEnd: string | undefined
  // Starts currently offered by this list. Cleared when the list unmounts.
  onRecommendedStartsChange?: (starts: string[]) => void
  // The visit being moved, so it does not block its own slot.
  excludeAppointmentId?: string
}

export function RecommendedAppointmentSlots(
  props: RecommendedAppointmentSlotsProps
): JSX.Element {
  const recommendedAppointmentSlotsQuery = useRecommendedAppointmentSlots(props)
  const onRecommendedStartsChange = props.onRecommendedStartsChange
  const onStartsChangeRef = useRef(onRecommendedStartsChange)
  onStartsChangeRef.current = onRecommendedStartsChange
  const recommendedSlots = recommendedAppointmentSlotsQuery.data
  useEffect(() => {
    onStartsChangeRef.current?.(
      recommendedSlots?.flatMap((slot) =>
        slot.period.start ? [slot.period.start] : []
      ) ?? []
    )
  }, [recommendedSlots])
  useEffect(() => () => onStartsChangeRef.current?.([]), [])

  return (
    <FhirQueryLoader query={recommendedAppointmentSlotsQuery}>
      {(data) => {
        if (!data?.length) {
          return null
        }

        const appointmentSlots = data ?? []
        const dayGroupedAppointmentSlots = groupBy(appointmentSlots, (v) =>
          startOfDay(v.period.start ?? now())
        )

        return (
          <div className="flex flex-col gap-2">
            <p className="text-3xs">
              Select any of the first available appointment slots:
            </p>
            <div className="flex justify-between gap-2">
              {Object.entries(dayGroupedAppointmentSlots).map(
                ([day, slots]) => (
                  <div key={day} className="flex flex-col gap-2">
                    <p className="text-xs">{format(day, 'MMM dd EEE')}</p>
                    <div className="max-h-32 overflow-y-scroll">
                      {slots.map((slot) => {
                        if (!slot.period.start || !slot.period.end) {
                          return null
                        }
                        const isSelected =
                          props.selectedStart &&
                          isSameMinute(
                            slot.period.start,
                            props.selectedStart
                          ) &&
                          props.selectedEnd &&
                          isSameMinute(slot.period.end, props.selectedEnd)

                        return (
                          <Button
                            variant="outline"
                            size="sm"
                            key={slot.period.start}
                            onClick={(evt) => {
                              evt.preventDefault()
                              evt.stopPropagation()
                              props.onAppointmentSlotSelected(slot)
                            }}
                            className={cn(
                              'h-6 w-full py-0 text-3xs',
                              isSelected &&
                                'sticky top-0 bottom-0 z-50 bg-gray-100'
                            )}
                          >
                            {formatTime(slot.period.start)}
                          </Button>
                        )
                      })}
                    </div>
                  </div>
                )
              )}
            </div>
          </div>
        )
      }}
    </FhirQueryLoader>
  )
}

const useRecommendedAppointmentSlots = (
  {
    patient,
    healthcareService,
    practitioner,
    start,
    excludeAppointmentId,
  }: {
    patient?: Reference<Patient>
    healthcareService?: Reference<HealthcareService>
    practitioner?: Reference<Practitioner>
    start?: string
    excludeAppointmentId?: string
  },
  options: Omit<
    UseQueryOptions<appointments.AvailableAppointmentSlot[] | undefined>,
    'queryKey' | 'queryFn'
  > = {}
): UseQueryResult<appointments.AvailableAppointmentSlot[] | undefined> => {
  const medplum = useMedplum()

  const _now = useMemo(() => now(), [])
  const weekdays = getNextNWorkdays(4, start ?? addDays(_now, 1).toISOString())
  const _start = startOfDay(weekdays[0]).toISOString()
  const _end = endOfDay(weekdays[weekdays.length - 1]).toISOString()

  const result = useQuery({
    ...options,
    // oxlint-disable-next-line @tanstack/query/exhaustive-deps
    queryKey: cacheKeys.recommendedAppointmentSlots({
      patient,
      healthcareService,
      practitioner,
      start: _start,
      end: _end,
      excludeAppointmentId,
    }),
    queryFn: async () => {
      if (!patient || !healthcareService || !practitioner || !_start || !_end) {
        return []
      }

      return appointments.getAvailableAppointmentSlots(medplum, {
        patient,
        healthcareService,
        practitioner,
        start: _start,
        end: _end,
        excludeAppointmentId,
      })
    },
  })

  useErrorToast(
    result.error &&
      `Error accessing recommended appointments: ${result.error.message ?? 'Unknown error'}`
  )

  return result
}
